import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row } from "@tanstack/react-table";
import { Performance, Report, Schedule } from "@dreamhub/designsystem/icons";
import DataTable from "@/components/controls/DataTable/DataTable";
import ShowAsDropdown, {
  ShowAsOption
} from "@/components/global/Chat/ShowAsDropdown";
import {
  ReportsTableModal,
  REPORTS_TABLE_CLASSNAME,
  REPORTS_TABLE_HEADER_CLASSNAME
} from "@/components/global/Chat/components/dreamerReports/ReportsTableModal";
import { SavedReport } from "@/entities/savedReports/types";
import { useSavedReportsInfinite } from "@/entities/savedReports/hooks";
import { ReportPreviewHost } from "@/components/global/Chat/components/dreamerReports/ReportPreviewHost";
import { ReportsEmptyState } from "@/components/global/Chat/components/dreamerReports/ReportsEmptyState";
import { ReportRowModals } from "@/components/global/Chat/components/dreamerReports/ReportRowModals";
import { useRenameClickGuard } from "@/components/global/Chat/components/dreamerReports/useRenameClickGuard";
import { useReportSurfaceTargets } from "@/components/global/Chat/components/dreamerReports/useReportSurfaceTargets";
import { useExpandedReportColumns } from "@/components/global/Chat/components/dreamerReports/useExpandedReportColumns";
import {
  EXPANDED_TRANSLATION_KEY,
  TRANSLATION_KEY
} from "@/components/global/Chat/components/dreamerReports/consts";

const EXPANDED_PAGE_SIZE = 50;

type ShowMode = "all" | "scheduled";

const SHOW_OPTIONS: ShowAsOption<ShowMode>[] = [
  {
    mode: "all",
    icon: <Performance />,
    labelKey: `${EXPANDED_TRANSLATION_KEY}.showOptions.all`
  },
  {
    mode: "scheduled",
    icon: <Schedule />,
    labelKey: `${EXPANDED_TRANSLATION_KEY}.showOptions.scheduled`
  }
];

interface ExpandedReportsModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** The panel's filter, which the table opens on. */
  scheduledOnly: boolean;
}

/**
 * The full-window view of the reports list, opened from the panel header. Same
 * rows and same action set as the drawer, laid out as a table. It opens on the
 * panel's filter and then filters on its own, without moving the panel's.
 */
export const ExpandedReportsModal: React.FC<ExpandedReportsModalProps> = ({
  isOpen,
  setIsOpen,
  scheduledOnly: panelScheduledOnly
}) => {
  const { t } = useTranslation();
  const [showMode, setShowMode] = useState<ShowMode>(
    panelScheduledOnly ? "scheduled" : "all"
  );
  useEffect(() => {
    if (isOpen) setShowMode(panelScheduledOnly ? "scheduled" : "all");
  }, [isOpen, panelScheduledOnly]);
  const scheduledOnly = showMode === "scheduled";
  const [renameTargetReportId, setRenameTargetReportId] = useState<
    string | null
  >(null);
  const { rowHandlers, previewHostProps, rowModalsProps, clearTargets } =
    useReportSurfaceTargets();
  const {
    reports,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useSavedReportsInfinite({
    scheduledOnly,
    pageSize: EXPANDED_PAGE_SIZE,
    enabled: isOpen
  });

  const handleStartRename = useCallback(
    (report: SavedReport) => setRenameTargetReportId(report.id),
    []
  );
  const handleFinishRename = useCallback(
    () => setRenameTargetReportId(null),
    []
  );
  // Leaving for the chat, from the empty state.
  const handleLeave = useCallback(() => setIsOpen(false), [setIsOpen]);

  // The preview and the row dialogs are siblings of this modal rather than its
  // children, so closing it would otherwise leave whatever the user opened from
  // a row stranded on screen, over the panel they came back to.
  useEffect(() => {
    if (isOpen) return;
    clearTargets();
    setRenameTargetReportId(null);
  }, [isOpen, clearTargets]);

  const columns = useExpandedReportColumns({
    t,
    renameTargetReportId,
    onStartRename: handleStartRename,
    onFinishRename: handleFinishRename,
    ...rowHandlers
  });

  // The whole row opens the report, except the one being renamed and the click
  // that ends a rename.
  const { onPointerDownCapture, onClickCapture, consumeRenameClick } =
    useRenameClickGuard(renameTargetReportId !== null);
  const handleRowClick = useCallback(
    (row: Row<SavedReport>) => {
      if (consumeRenameClick()) return;
      if (row.original.id === renameTargetReportId) return;
      rowHandlers.onOpen(row.original);
    },
    [renameTargetReportId, rowHandlers, consumeRenameClick]
  );

  const tableOptions = useMemo(
    () => ({ getRowId: (report: SavedReport) => report.id }),
    []
  );

  const hasNothingToShow = !isLoading && reports.length === 0;

  return (
    <>
      <ReportsTableModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        titleIcon={<Report />}
        title={t(`${EXPANDED_TRANSLATION_KEY}.title`)}
        toolbar={
          <div className="flex shrink-0 justify-end border-b border-border-border1 px-4 pb-4">
            <ShowAsDropdown
              selectedMode={showMode}
              options={SHOW_OPTIONS}
              onSelect={setShowMode}
              labelKey={`${EXPANDED_TRANSLATION_KEY}.show`}
              dataTest="expanded-reports-show"
            />
          </div>
        }
        emptyState={
          // The Scheduled filter reads as the Reports tab's does.
          hasNothingToShow && !error && scheduledOnly ? (
            <ReportsEmptyState scheduledOnly onLeave={handleLeave} />
          ) : undefined
        }
        emptyText={
          hasNothingToShow
            ? t(
                error
                  ? `${TRANSLATION_KEY}.loadFailed`
                  : `${TRANSLATION_KEY}.empty`
              )
            : undefined
        }
        table={
          // `contents` keeps this wrapper out of the table's layout; it only
          // sees the press before the rename input's blur, and the click after.
          <div
            className="contents"
            onPointerDownCapture={onPointerDownCapture}
            onClickCapture={onClickCapture}
          >
            <DataTable
              dataTest="expanded-reports-table"
              columns={columns}
              onRowClick={handleRowClick}
              data={reports}
              tableOptions={tableOptions}
              isDataLoading={isLoading}
              isInfiniteScroll
              hasMore={hasNextPage && !error}
              isLoadingMore={isFetchingNextPage}
              onLoadMore={fetchNextPage}
              headerClassName={REPORTS_TABLE_HEADER_CLASSNAME}
              className={REPORTS_TABLE_CLASSNAME}
            />
          </div>
        }
        dataTest="expanded-reports-modal"
      />
      {/* Stacks over the table, which stays open beneath the report. */}
      <ReportPreviewHost {...previewHostProps} />
      <ReportRowModals {...rowModalsProps} />
    </>
  );
};
